import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { pushAnalyticsEvent } from '@allesauto/utils/analytics';
import { SupportedCurrencies } from '@allesauto/utils/consts';
import { useRouter } from 'next/router';
import React, { FC, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import DealerFinder from '@/components/DealerFinder/DealerFinder';
import OverlaySuccess from '@/components/forms/overlaySuccess/OverlaySuccess';
import Footer from '@/components/OverlayFormFooter/OverlayFormFooter';

import { closeOverlay, openOverlay } from '@/store/redux/actions/app';
import { setVehicle } from '@/store/redux/slices/pages/pdp';

import CarConfiguratorPriceOffer from '../CarConfiguratorPriceOffer/CarConfiguratorPriceOffer';
import { getCarConfigMonthlyPrice } from '../../../../../../packages/allesauto-core/src/lib/header/common/funcs';

interface CarConfiguratorDealerOverlayProps {
  monthlyPrice: number;
  make?: string;
  executeConfiguratorData: (
    dealerId: string,
    configuratorId: string
  ) => Promise<SchemaTypes.Vehicle | null | undefined>;
  isConfigurableCar: boolean;
}

const CarConfiguratorDealerOverlay: FC<CarConfiguratorDealerOverlayProps> = ({
  monthlyPrice,
  make,
  executeConfiguratorData,
  isConfigurableCar,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const router = useRouter();
  const [dealerId, setDealerId] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const className = {
    containerOverlay: 'mb-[213px]',
    textContainer: 'font-regular mb-40px text-grey60',
    privacyDescription: 'text-p3 mt-60px text-grey60',
  };

  const openConfiguratorPriceOffer = useCallback(
    (
      modelName: string,
      images?: SchemaTypes.VehicleImage[] | null,
      price?: number | null,
      configMonthlyPrice?: number | null
    ) => {
      dispatch(
        openOverlay({
          children: (
            <OverlaySuccess
              bannerHeadline={translate(
                intl,
                'ui.PDP.CAR_CONFIGURATOR.DEALER_SELECT_SUCCESS_TITLE'
              )}
              bodyHeadline={translate(
                intl,
                'ui.PDP.CAR_CONFIGURATOR.DEALER_SELECT_SUCCESS_DESCRIPTION'
              )}
              bottomText={translate(
                intl,
                'ui.PDP.CAR_CONFIGURATOR.DEALER_SELECT_SUCCESS_DESCRIPTION_TEXT'
              )}
              customDescription={
                <CarConfiguratorPriceOffer
                  images={images}
                  make={make}
                  modelName={modelName}
                  price={price ?? 0}
                  monthlyPrice={
                    configMonthlyPrice ? configMonthlyPrice : monthlyPrice
                  }
                />
              }
              ctaLabel={translate(intl, 'ui.PAYMENT_OPTIONS.BUTTON_TEXT')}
              onBtnClick={() => dispatch(closeOverlay())}
            />
          ),
          title: '',
          withPaddings: false,
        })
      );
    },
    [dispatch, intl, make, monthlyPrice]
  );

  const handleDealerSelection = useCallback(async () => {
    try {
      if (
        dealerId &&
        router.query.configuratorId &&
        typeof router.query.configuratorId === 'string'
      ) {
        setIsLoading(true);
        const configurableCar = await executeConfiguratorData(
          dealerId,
          router.query.configuratorId
        );
        if (configurableCar) {
          const {
            price,
            modelName,
            images,
            vehicleDetail,
            id: vehicleId,
            makeName,
          } = configurableCar;

          const configMonthlyPrice = getCarConfigMonthlyPrice(
            configurableCar?.financingDetails
          );

          await router.replace(
            {
              pathname: router.asPath.split('?')[0],
              query: {
                configuratorId: router.query.configuratorId,
                dealerId: dealerId,
              },
            },
            undefined,
            { shallow: true }
          );
          pushAnalyticsEvent({
            event: 'configurator_success',
            dealer_name: vehicleDetail?.dealer?.name,
            ecommerce: {
              currency: SupportedCurrencies.EURO,
              value: price,
              items: [
                {
                  item_name: `${makeName?.toLowerCase()} ${modelName?.toLowerCase()}`,
                  item_id: vehicleId,
                  price: price, //price of the car
                  index: 0,
                  quantity: 1,
                },
              ],
            },
          });
          dispatch(
            setVehicle({
              price: price ?? 0,
              persistentState: false,
            })
          );
          setIsLoading(false);
          dispatch(closeOverlay());
          setTimeout(
            () =>
              openConfiguratorPriceOffer(
                modelName || '',
                images as SchemaTypes.VehicleImage[],
                price,
                configMonthlyPrice
              ),
            100
          );
        }
      }
      setIsLoading(false);
    } catch (err) {
      //Global error handler
    }
  }, [
    dealerId,
    router,
    executeConfiguratorData,
    dispatch,
    openConfiguratorPriceOffer,
  ]);

  return (
    <div className={className.containerOverlay}>
      <div className={className.textContainer}>
        {translate(intl, 'ui.PDP.CAR_CONFIGURATOR.DEALER_DESCRIPTION')}
      </div>
      <DealerFinder
        onDealerSelect={(dealer) => setDealerId(dealer.id)}
        optionalFilterParameters={{
          make,
        }}
        isConfigurableCar={isConfigurableCar}
      />
      <Footer
        buttonText={translate(intl, 'ui.PDP.DEALER_DETAILS.SELECT_DEALER')}
        onButtonClick={handleDealerSelection}
        isButtonDisabled={!dealerId}
        showButtonLoader={isLoading}
      ></Footer>
    </div>
  );
};

export default CarConfiguratorDealerOverlay;
